/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { ComponentPropsWithRef, FC, useMemo } from 'react';
import { Box } from '@mui/material';

import TestIds from '../../../../testIds';
import { getInitialsFromArray } from '../../../../util/formatting';

import './InitialsCircle.scss';

export interface IInitialsCircleProps extends ComponentPropsWithRef<'button'> {
  fullName: unknown[];
  invert?: boolean;
}

const InitialsCircle: FC<IInitialsCircleProps> = ({
  fullName,
  invert,
  type = 'button',
  className,
  ...buttonProps
}: IInitialsCircleProps): JSX.Element | undefined => {
  const initials = useMemo((): string => getInitialsFromArray(fullName), [fullName]);

  const initialDisplay = useMemo(
    (): JSX.Element => (
      <Box
        className={invert ? 'fl-initials-circle__light' : 'fl-initials-circle__dark'}
        data-testid={TestIds.feat.account.userAccount.initialsCircle.component}
      >
        {initials}
      </Box>
    ),
    [initials],
  );

  if (buttonProps.onClick) {
    return (
      <button
        data-testid={TestIds.feat.account.userAccount.initialsCircle.button}
        type={type}
        className={`${className} fl-initials-circle__button ${
          !initials ? 'fl-initials-circle__button-loading' : ''
        }`.trim()}
        aria-label={'Open the account menu'}
        {...buttonProps}
      >
        {initialDisplay}
      </button>
    );
  }

  return initialDisplay;
};

export default InitialsCircle;
